import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  GitCompare,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Calculator,
  Building2,
  Users,
  GraduationCap,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Info,
  Play,
  RotateCcw,
  Layers,
  Scale,
  Factory,
  BookOpen,
  Timer,
  Bookmark,
  BookmarkPlus,
  Sparkles,
  Trash2,
  Zap,
  Shield,
  Gauge,
  Building,
  BarChart3,
  PieChart,
  Activity,
  Star,
  Lightbulb,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { computeChainLeak } from '../lib/chainLeak';
import ValueTransferContextStrip from '../components/impact/ValueTransferContextStrip';

// System presets - focused on comparing execution/dampening factors
const SYSTEM_PRESETS = [
  {
    id: 'low_vs_high_support',
    name: 'Low vs High Program Support',
    description: 'Compare weak organizational support vs strong executive backing',
    icon: Shield,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    scenarioA: {
      name: 'Low Support',
      dampening_factors: {
        adoption_speed: 0.4,
        leadership_alignment: 0.5,
        operational_readiness: 0.45
      },
      current_maturity: 50,
      time_lag_config: {
        implementation_lag_months: 2,
        ramp_up_months: 3,
        include_decay: false,
        annual_decay_rate: 0.15
      }
    },
    scenarioB: {
      name: 'High Support',
      dampening_factors: {
        adoption_speed: 0.9,
        leadership_alignment: 0.95,
        operational_readiness: 0.9
      },
      current_maturity: 50,
      time_lag_config: {
        implementation_lag_months: 2,
        ramp_up_months: 3,
        include_decay: false,
        annual_decay_rate: 0.15
      }
    }
  },
  {
    id: 'fast_vs_slow_rollout',
    name: 'Fast vs Slow Rollout',
    description: 'Compare rapid deployment vs gradual implementation with longer ramp-up',
    icon: Zap,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    scenarioA: {
      name: 'Fast Rollout',
      dampening_factors: {
        adoption_speed: 0.85,
        leadership_alignment: 0.8,
        operational_readiness: 0.8
      },
      current_maturity: 50,
      time_lag_config: {
        implementation_lag_months: 1,
        ramp_up_months: 2,
        include_decay: false,
        annual_decay_rate: 0.15
      }
    },
    scenarioB: {
      name: 'Slow Rollout',
      dampening_factors: {
        adoption_speed: 0.6,
        leadership_alignment: 0.7,
        operational_readiness: 0.65
      },
      current_maturity: 50,
      time_lag_config: {
        implementation_lag_months: 4,
        ramp_up_months: 6,
        include_decay: true,
        annual_decay_rate: 0.2
      }
    }
  },
  {
    id: 'mature_vs_developing',
    name: 'Mature vs Developing L&D Function',
    description: 'Compare established learning culture vs emerging L&D capability',
    icon: Gauge,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    scenarioA: {
      name: 'Mature Organization',
      dampening_factors: {
        adoption_speed: 0.85,
        leadership_alignment: 0.9,
        operational_readiness: 0.85
      },
      current_maturity: 80,
      time_lag_config: {
        implementation_lag_months: 1,
        ramp_up_months: 2,
        include_decay: false,
        annual_decay_rate: 0.1
      }
    },
    scenarioB: {
      name: 'Developing Organization',
      dampening_factors: {
        adoption_speed: 0.55,
        leadership_alignment: 0.6,
        operational_readiness: 0.5
      },
      current_maturity: 30,
      time_lag_config: {
        implementation_lag_months: 3,
        ramp_up_months: 4,
        include_decay: false,
        annual_decay_rate: 0.15
      }
    }
  },
  {
    id: 'small_vs_large_scale',
    name: 'Small vs Large Organization',
    description: 'Compare agile small team vs enterprise-scale implementation',
    icon: Building,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    scenarioA: {
      name: 'Small Organization',
      headcount: 200,
      dampening_factors: {
        adoption_speed: 0.85,
        leadership_alignment: 0.9,
        operational_readiness: 0.85
      },
      current_maturity: 50,
      time_lag_config: {
        implementation_lag_months: 1,
        ramp_up_months: 2,
        include_decay: false,
        annual_decay_rate: 0.15
      }
    },
    scenarioB: {
      name: 'Large Enterprise',
      headcount: 50000,
      dampening_factors: {
        adoption_speed: 0.65,
        leadership_alignment: 0.7,
        operational_readiness: 0.6
      },
      current_maturity: 50,
      time_lag_config: {
        implementation_lag_months: 3,
        ramp_up_months: 5,
        include_decay: true,
        annual_decay_rate: 0.18
      }
    }
  },
  {
    id: 'with_vs_without_decay',
    name: 'Sustained vs Decaying Impact',
    description: 'Compare maintained learning effectiveness vs skill decay over time',
    icon: Timer,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    scenarioA: {
      name: 'Sustained (No Decay)',
      dampening_factors: {
        adoption_speed: 0.75,
        leadership_alignment: 0.8,
        operational_readiness: 0.75
      },
      current_maturity: 50,
      time_horizon_months: 24,
      time_lag_config: {
        implementation_lag_months: 2,
        ramp_up_months: 3,
        include_decay: false,
        annual_decay_rate: 0
      }
    },
    scenarioB: {
      name: 'With Skill Decay',
      dampening_factors: {
        adoption_speed: 0.75,
        leadership_alignment: 0.8,
        operational_readiness: 0.75
      },
      current_maturity: 50,
      time_horizon_months: 24,
      time_lag_config: {
        implementation_lag_months: 2,
        ramp_up_months: 3,
        include_decay: true,
        annual_decay_rate: 0.25
      }
    }
  }
];

// Analysis mode options
const ANALYSIS_MODES = [
  { id: 'single', label: 'Single Measure', description: 'Compare one LEI measure across different dampening configs' },
  { id: 'portfolio', label: 'Portfolio', description: 'Compare a portfolio of measures with different configs' },
  { id: 'matrix', label: 'Full Matrix', description: 'Compare full LEI-KPI matrix with different configs' }
];

// Industry options with modifiers (sorted A-Z by label)
const INDUSTRY_OPTIONS = [
  { value: 'education', label: 'Education', modifier: 1.05 },
  { value: 'energy', label: 'Energy & Utilities', modifier: 0.88 },
  { value: 'financial_services', label: 'Financial Services', modifier: 0.95 },
  { value: 'government', label: 'Government', modifier: 0.80 },
  { value: 'healthcare', label: 'Healthcare', modifier: 0.85 },
  { value: 'hospitality', label: 'Hospitality', modifier: 0.70 },
  { value: 'manufacturing', label: 'Manufacturing', modifier: 0.90 },
  { value: 'nonprofit', label: 'Non-Profit', modifier: 0.90 },
  { value: 'other', label: 'Other', modifier: 1.00 },
  { value: 'professional_services', label: 'Professional Services', modifier: 1.10 },
  { value: 'retail', label: 'Retail', modifier: 0.75 },
  { value: 'technology', label: 'Technology', modifier: 1.15 },
  { value: 'telecommunications', label: 'Telecommunications', modifier: 1.00 }
];

// Learning modality options with effectiveness ratings (sorted A-Z by label)
// Higher effectiveness = better transfer of learning to business outcomes
const MODALITY_OPTIONS = [
  { 
    value: 'blended', 
    label: 'Blended Learning', 
    effectiveness: 120,
    factor: 0.80,
    description: 'Combines multiple methods for maximum retention and application',
    research: 'Studies show 15-25% higher retention vs single-modality approaches'
  },
  { 
    value: 'coaching_mentoring', 
    label: 'Coaching & Mentoring', 
    effectiveness: 108,
    factor: 0.85,
    description: 'Personalized development with ongoing support',
    research: 'High impact on behavior change and leadership development'
  },
  { 
    value: 'in_person_ilt', 
    label: 'In-Person Instructor-Led', 
    effectiveness: 100,
    factor: 0.85,
    description: 'Traditional classroom with live instruction',
    research: 'Baseline effectiveness - good for complex topics'
  },
  { 
    value: 'microlearning', 
    label: 'Microlearning', 
    effectiveness: 75,
    factor: 0.65,
    description: 'Short, focused learning modules',
    research: 'Best for reinforcement and just-in-time learning'
  },
  { 
    value: 'on_the_job', 
    label: 'On-the-Job Coaching', 
    effectiveness: 115,
    factor: 0.95,
    description: 'Direct application in work context with expert guidance',
    research: 'Immediate skill transfer with 70-20-10 model foundation'
  },
  { 
    value: 'peer_learning', 
    label: 'Peer/Social Learning', 
    effectiveness: 95,
    factor: 0.78,
    description: 'Collaborative learning from colleagues',
    research: 'Strong for culture building and tacit knowledge'
  },
  { 
    value: 'self_paced_elearning', 
    label: 'Self-Paced eLearning', 
    effectiveness: 60,
    factor: 0.55,
    description: 'Asynchronous online modules',
    research: 'Completion rates often 20-30%, but scalable'
  },
  { 
    value: 'simulation', 
    label: 'Simulation/VR Training', 
    effectiveness: 110,
    factor: 0.88,
    description: 'Immersive practice in realistic scenarios',
    research: 'Up to 4x faster learning for complex skills'
  },
  { 
    value: 'virtual_ilt', 
    label: 'Virtual Instructor-Led', 
    effectiveness: 85,
    factor: 0.75,
    description: 'Live online instruction with interaction',
    research: '10-15% lower retention than in-person ILT'
  }
];

// Default scenario configuration
const getDefaultScenarioConfig = (profileDefaults) => ({
  // Base settings
  headcount: profileDefaults?.total_workforce || 2000,
  revenue_per_employee: profileDefaults?.revenue_per_employee || 150000,
  intervention_cost: profileDefaults?.default_intervention_budget || 500000,
  lei_improvement: 5,
  elasticity_scenario: profileDefaults?.preferred_elasticity_scenario || 'moderate',
  current_maturity: profileDefaults?.current_maturity || 50,
  
  // Dampening factors
  dampening_factors: {
    adoption_speed: profileDefaults?.dampening_factors?.adoption_speed || 0.7,
    leadership_alignment: profileDefaults?.dampening_factors?.leadership_alignment || 0.8,
    operational_readiness: profileDefaults?.dampening_factors?.operational_readiness || 0.75
  },
  
  // New dampening settings
  industry_type: profileDefaults?.industry_type || 'technology',
  learning_modality: profileDefaults?.primary_learning_modality || 'blended',
  scale_dampening_config: profileDefaults?.scale_dampening_config || { mode: 'automatic' },
  time_lag_config: profileDefaults?.time_lag_config || {
    implementation_lag_months: 2,
    ramp_up_months: 3,
    include_decay: false,
    annual_decay_rate: 0.15
  },
  time_horizon_months: 12,
  
  // For single measure mode
  lei_measure_id: 'training_performance',
  kpi_ids: ['productivity', 'quality', 'engagement'],
  
  // For portfolio/matrix mode
  measures: [
    { lei_measure_id: 'training_scope', lei_improvement: 5, enabled: true },
    { lei_measure_id: 'training_performance', lei_improvement: 5, enabled: true }
  ],
  kpi_id: 'productivity'
});

const DampeningComparisonPage = () => {
  const navigate = useNavigate();
  const { user, currency } = useApp();
  const profileDefaults = user?.profile;

  // State
  const [analysisMode, setAnalysisMode] = useState('single');
  const [scenarioA, setScenarioA] = useState(null);
  const [scenarioB, setScenarioB] = useState(null);
  const [resultsA, setResultsA] = useState(null);
  const [resultsB, setResultsB] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Live Mission Control constraint — powers the context strip and
  // the "Constraint lever" slider highlights.
  const [healthDeltas, setHealthDeltas] = useState(null);
  useEffect(() => {
    api.get('/health/quarter-deltas').then(r => setHealthDeltas(r.data)).catch(() => {});
  }, []);
  const chainLeak = computeChainLeak(healthDeltas?.metrics);
  const leakPct = chainLeak ? Math.abs(Math.round(chainLeak.deltaPct)) : null;
  // Constraint stage → the scenario lever that most directly models it.
  const CONSTRAINT_LEVERS = {
    capability: 'current_maturity',
    behavior: 'adoption_speed',
    outcomes: 'leadership_alignment',
    financial: 'leadership_alignment',
  };
  const constraintLever = CONSTRAINT_LEVERS[chainLeak?.key] || null;
  // The constraint levers live inside Advanced Settings — open it by
  // default so the highlighted slider is immediately visible.
  useEffect(() => {
    if (constraintLever) setShowAdvanced(true);
  }, [constraintLever]);
  const [expandedFactors, setExpandedFactors] = useState({ A: false, B: false });
  
  // LEI Measure and Mode state
  const [leiMeasures, setLeiMeasures] = useState([]);
  const [leiModeA, setLeiModeA] = useState('simple'); // 'simple', 'advanced', 'import'
  const [leiModeB, setLeiModeB] = useState('simple');
  const [importedBaselines, setImportedBaselines] = useState(null);
  const [isLoadingBaselines, setIsLoadingBaselines] = useState(false);
  
  // Preset state
  const [showPresets, setShowPresets] = useState(true);
  const [userPresets, setUserPresets] = useState([]);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [currentPreset, setCurrentPreset] = useState(null); // Track loaded preset
  
  // Analytics state
  const [personalAnalytics, setPersonalAnalytics] = useState(null);
  const [aggregateAnalytics, setAggregateAnalytics] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [goalsSummary, setGoalsSummary] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  // Load user presets and analytics on mount
  useEffect(() => {
    loadUserPresets();
    loadAnalytics();
    loadLEIMeasures();
  }, []);
  
  const loadLEIMeasures = async () => {
    try {
      const response = await api.get(`/lei-measures`);
      setLeiMeasures(response.data);
    } catch (error) {
      logger.error('Error loading LEI measures:', error);
    }
  };
  
  const loadImportedBaselines = useCallback(async () => {
    if (importedBaselines) return; // Already loaded
    try {
      setIsLoadingBaselines(true);
      const response = await api.get(`/import/data/lei-baselines/latest`);
      setImportedBaselines(response.data);
      
      // Auto-fill scenario values if import mode is active
      if (response.data?.baselines) {
        if (leiModeA === 'import' && scenarioA?.lei_measure_id) {
          const baseline = response.data.baselines[scenarioA.lei_measure_id];
          if (baseline?.calculated_score) {
            updateScenario('setScenarioA', 'current_maturity', baseline.calculated_score);
          }
        }
        if (leiModeB === 'import' && scenarioB?.lei_measure_id) {
          const baseline = response.data.baselines[scenarioB.lei_measure_id];
          if (baseline?.calculated_score) {
            updateScenario('setScenarioB', 'current_maturity', baseline.calculated_score);
          }
        }
      }
    } catch (error) {
      logger.error('Error loading imported baselines:', error);
    } finally {
      setIsLoadingBaselines(false);
    }
  }, [importedBaselines, leiModeA, leiModeB, scenarioA, scenarioB]);

  // Load imported baselines when import mode is selected
  useEffect(() => {
    if (leiModeA === 'import' || leiModeB === 'import') {
      loadImportedBaselines();
    }
  }, [leiModeA, leiModeB, loadImportedBaselines]);

  const loadAnalytics = async () => {
    try {
      setIsLoadingAnalytics(true);
      const [personalRes, aggregateRes, trendsRes, goalsRes] = await Promise.all([
        api.get(`/comparison-analytics/personal`),
        api.get(`/comparison-analytics/aggregate`),
        api.get(`/comparison-analytics/trends?period=30d`),
        api.get(`/goals/summary`).catch(() => ({ data: null }))
      ]);
      setPersonalAnalytics(personalRes.data);
      setAggregateAnalytics(aggregateRes.data);
      setTrendsData(trendsRes.data);
      setGoalsSummary(goalsRes.data);
    } catch (error) {
      logger.error('Error loading analytics:', error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const trackComparison = async (resA, resB) => {
    try {
      const roiA = resA.total_roi_percent || resA.roi_percent || 0;
      const roiB = resB.total_roi_percent || resB.roi_percent || 0;
      const impactA = resA.total_financial_impact || resA.financial_impact || 0;
      const impactB = resB.total_financial_impact || resB.financial_impact || 0;
      const combinedA = resA.combined_adjustment_factor || 1;
      const combinedB = resB.combined_adjustment_factor || 1;
      
      await api.post(`/comparison-analytics/track`, {
        preset_id: currentPreset?.id || currentPreset?.preset_id || null,
        preset_name: currentPreset?.name || null,
        preset_type: currentPreset ? (SYSTEM_PRESETS.find(p => p.id === currentPreset.id) ? 'system' : 'user') : 'custom',
        analysis_mode: analysisMode,
        scenarioA,
        scenarioB,
        resultsA: resA,
        resultsB: resB,
        roi_difference_percent: roiA !== 0 ? ((roiB - roiA) / Math.abs(roiA)) * 100 : (roiB > 0 ? 100 : 0),
        impact_difference: impactB - impactA,
        combined_adjustment_a: combinedA,
        combined_adjustment_b: combinedB
      });
      
      // Refresh analytics after tracking
      loadAnalytics();
    } catch (error) {
      logger.error('Error tracking comparison:', error);
      // Don't show error to user - tracking is optional
    }
  };

  const loadUserPresets = async () => {
    try {
      setIsLoadingPresets(true);
      const response = await api.get(`/comparison-analytics/presets`);
      setUserPresets(response.data || []);
    } catch (error) {
      logger.error('Error loading presets:', error);
      // Silently fail - presets are optional
    } finally {
      setIsLoadingPresets(false);
    }
  };

  const saveUserPreset = async () => {
    if (!newPresetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    try {
      setIsSavingPreset(true);
      const presetData = {
        name: newPresetName.trim(),
        description: newPresetDescription.trim(),
        scenarioA: {
          ...scenarioA,
          name: scenarioA.name || 'Scenario A'
        },
        scenarioB: {
          ...scenarioB,
          name: scenarioB.name || 'Scenario B'
        },
        analysisMode
      };

      await api.post(`/comparison-analytics/presets`, presetData);
      toast.success('Preset saved successfully!');
      setSavePresetDialogOpen(false);
      setNewPresetName('');
      setNewPresetDescription('');
      loadUserPresets();
    } catch (error) {
      logger.error('Error saving preset:', error);
      toast.error('Failed to save preset');
    } finally {
      setIsSavingPreset(false);
    }
  };

  const deleteUserPreset = async (presetId) => {
    try {
      await api.delete(`/comparison-analytics/presets/${presetId}`);
      toast.success('Preset deleted');
      loadUserPresets();
    } catch (error) {
      logger.error('Error deleting preset:', error);
      toast.error('Failed to delete preset');
    }
  };

  const loadPreset = (preset, isSystemPreset = false) => {
    const defaultConfig = getDefaultScenarioConfig(profileDefaults);
    
    // Merge preset settings with defaults
    const newScenarioA = {
      ...defaultConfig,
      ...preset.scenarioA,
      name: preset.scenarioA.name || 'Scenario A',
      dampening_factors: {
        ...defaultConfig.dampening_factors,
        ...preset.scenarioA.dampening_factors
      },
      time_lag_config: {
        ...defaultConfig.time_lag_config,
        ...preset.scenarioA.time_lag_config
      }
    };

    const newScenarioB = {
      ...defaultConfig,
      ...preset.scenarioB,
      name: preset.scenarioB.name || 'Scenario B',
      dampening_factors: {
        ...defaultConfig.dampening_factors,
        ...preset.scenarioB.dampening_factors
      },
      time_lag_config: {
        ...defaultConfig.time_lag_config,
        ...preset.scenarioB.time_lag_config
      }
    };

    setScenarioA(newScenarioA);
    setScenarioB(newScenarioB);
    setResultsA(null);
    setResultsB(null);
    setCurrentPreset(preset); // Track loaded preset
    
    if (preset.analysisMode) {
      setAnalysisMode(preset.analysisMode);
    }

    toast.success(`Loaded preset: ${preset.name}`);
  };

  // Initialize scenarios from profile defaults
  useEffect(() => {
    const defaultConfig = getDefaultScenarioConfig(profileDefaults);
    setScenarioA({ ...defaultConfig, name: 'Scenario A' });
    
    // Scenario B starts with different settings for comparison
    setScenarioB({
      ...defaultConfig,
      name: 'Scenario B',
      industry_type: 'healthcare',
      learning_modality: 'virtual_ilt',
      headcount: 10000
    });
  }, [profileDefaults]);

  // Update scenario config
  const updateScenario = (setterKey, field, value) => {
    const setter = setterKey === 'setScenarioA' || setterKey === 'A' ? setScenarioA : setScenarioB;
    setter(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return { ...prev, [parent]: { ...prev[parent], [child]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  // Run comparison
  const runComparison = async () => {
    if (!scenarioA || !scenarioB) {
      toast.error('Please configure both scenarios');
      return;
    }

    setIsCalculating(true);
    setResultsA(null);
    setResultsB(null);

    try {
      let endpointA, endpointB, payloadA, payloadB;

      if (analysisMode === 'single') {
        endpointA = `/calculate-single-measure`;
        endpointB = `/calculate-single-measure`;
        payloadA = buildSingleMeasurePayload(scenarioA);
        payloadB = buildSingleMeasurePayload(scenarioB);
      } else if (analysisMode === 'portfolio') {
        endpointA = `/calculate-portfolio`;
        endpointB = `/calculate-portfolio`;
        payloadA = buildPortfolioPayload(scenarioA);
        payloadB = buildPortfolioPayload(scenarioB);
      } else {
        endpointA = `/calculate-full-matrix`;
        endpointB = `/calculate-full-matrix`;
        payloadA = buildMatrixPayload(scenarioA);
        payloadB = buildMatrixPayload(scenarioB);
      }

      const [resA, resB] = await Promise.all([
        api.post(endpointA, payloadA),
        api.post(endpointB, payloadB)
      ]);

      setResultsA(resA.data);
      setResultsB(resB.data);
      toast.success('Comparison complete!');
      
      // Track comparison for analytics
      trackComparison(resA.data, resB.data);
    } catch (error) {
      logger.error('Comparison error:', error);
      toast.error('Failed to run comparison: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsCalculating(false);
    }
  };

  // Build payloads for different modes
  const buildSingleMeasurePayload = (config) => ({
    lei_measure_id: config.lei_measure_id,
    kpi_ids: config.kpi_ids,
    lei_improvement: config.lei_improvement,
    elasticity_scenario: config.elasticity_scenario,
    dampening_factors: config.dampening_factors,
    current_maturity: config.current_maturity,
    intervention_cost: config.intervention_cost,
    headcount: config.headcount,
    time_horizon_months: config.time_horizon_months,
    scale_dampening_config: config.scale_dampening_config,
    industry_type: config.industry_type,
    learning_modality: config.learning_modality,
    time_lag_config: config.time_lag_config
  });

  const buildPortfolioPayload = (config) => ({
    measures: config.measures,
    kpi_id: config.kpi_id,
    elasticity_scenario: config.elasticity_scenario,
    intervention_cost: config.intervention_cost,
    current_maturity: config.current_maturity,
    dampening_factors: config.dampening_factors,
    headcount: config.headcount,
    revenue_per_employee: config.revenue_per_employee,
    scale_dampening_config: config.scale_dampening_config,
    industry_type: config.industry_type,
    learning_modality: config.learning_modality,
    time_horizon_months: config.time_horizon_months,
    time_lag_config: config.time_lag_config
  });

  const buildMatrixPayload = (config) => ({
    measures: config.measures,
    elasticity_scenario: config.elasticity_scenario,
    intervention_cost: config.intervention_cost,
    current_maturity: config.current_maturity,
    dampening_factors: config.dampening_factors,
    headcount: config.headcount,
    revenue_per_employee: config.revenue_per_employee,
    scale_dampening_config: config.scale_dampening_config,
    industry_type: config.industry_type,
    learning_modality: config.learning_modality,
    time_horizon_months: config.time_horizon_months,
    time_lag_config: config.time_lag_config
  });

  // Reset to defaults
  const resetScenarios = () => {
    const defaultConfig = getDefaultScenarioConfig(profileDefaults);
    setScenarioA({ ...defaultConfig, name: 'Scenario A' });
    setScenarioB({
      ...defaultConfig,
      name: 'Scenario B',
      industry_type: 'healthcare',
      learning_modality: 'virtual_ilt',
      headcount: 10000
    });
    setResultsA(null);
    setResultsB(null);
    setCurrentPreset(null); // Clear loaded preset
  };

  // Calculate differences
  const getDifference = (valA, valB) => {
    if (valA === undefined || valB === undefined) return { value: 0, percent: 0 };
    const diff = valB - valA;
    const percent = valA !== 0 ? ((valB - valA) / Math.abs(valA)) * 100 : 0;
    return { value: diff, percent };
  };

  // Get factor from results
  const getFactorValue = (results, factorKey) => {
    if (!results) return null;
    switch (factorKey) {
      case 'scale': return results.scale_dampening?.factor;
      case 'industry': return results.industry_modifier?.modifier;
      case 'modality': return results.learning_modality?.factor;
      case 'time_lag': return results.time_lag?.factor;
      case 'combined': return results.combined_adjustment_factor;
      default: return null;
    }
  };

  // Render scenario config panel
  const renderScenarioConfig = (scenario, scenarioKey, label, color) => {
    if (!scenario) return null;
    const setterKey = scenarioKey === 'A' ? 'setScenarioA' : 'setScenarioB';
    const currentLeiMode = scenarioKey === 'A' ? leiModeA : leiModeB;
    const setLeiMode = scenarioKey === 'A' ? setLeiModeA : setLeiModeB;

    return (
      <Card className={`border-2 ${color === 'blue' ? 'border-blue-200 bg-blue-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-lg ${color === 'blue' ? 'text-blue-700' : 'text-amber-700'}`}>
              {label}
            </CardTitle>
            <Badge variant="outline" className={color === 'blue' ? 'border-blue-300 text-blue-600' : 'border-amber-300 text-amber-600'}>
              {INDUSTRY_OPTIONS.find(i => i.value === scenario.industry_type)?.label || 'Unknown'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Organization Size */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" />
              Headcount
            </Label>
            <Input
              type="number"
              value={scenario.headcount}
              onChange={(e) => updateScenario(setterKey, 'headcount', parseInt(e.target.value) || 0)}
              className="bg-white"
            />
          </div>

          {/* Industry Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Factory className="h-4 w-4" />
              Industry
            </Label>
            <Select
              value={scenario.industry_type}
              onValueChange={(v) => updateScenario(setterKey, 'industry_type', v)}
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label} ({(opt.modifier * 100).toFixed(0)}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Learning Modality with Effectiveness Tooltip */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <GraduationCap className="h-4 w-4" />
              Learning Modality
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3.5 w-3.5 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Different learning modalities have varying effectiveness in transferring skills to business outcomes. Higher % = greater impact.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Select
              value={scenario.learning_modality}
              onValueChange={(v) => updateScenario(setterKey, 'learning_modality', v)}
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODALITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="cursor-pointer">
                    <div className="flex items-center justify-between w-full gap-2">
                      <span>{opt.label}</span>
                      <Badge 
                        variant="outline" 
                        className={`text-[10px] ${
                          opt.effectiveness >= 110 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          opt.effectiveness >= 90 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          opt.effectiveness >= 70 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {opt.effectiveness}% eff.
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Show selected modality effectiveness with description */}
            {scenario.learning_modality && (
              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span>Effectiveness:</span>
                  <span className={`font-medium ${
                    MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.effectiveness >= 110 ? 'text-emerald-600' :
                    MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.effectiveness >= 90 ? 'text-blue-600' :
                    'text-amber-600'
                  }`}>
                    {MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.effectiveness || 100}%
                  </span>
                </div>
                <p className="text-slate-400 text-[10px]">
                  {MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.description || ''}
                </p>
                <p className="text-slate-400 text-[10px] italic">
                  {MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.research || ''}
                </p>
              </div>
            )}
          </div>

          {/* Time Horizon */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4" />
              Time Horizon: {scenario.time_horizon_months} months
            </Label>
            <Slider
              value={[scenario.time_horizon_months]}
              onValueChange={([v]) => updateScenario(setterKey, 'time_horizon_months', v)}
              min={6}
              max={36}
              step={6}
              className="py-2"
            />
          </div>

          {/* Investment Cost */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <DollarSign className="h-4 w-4" />
              Investment
            </Label>
            <Input
              type="number"
              value={scenario.intervention_cost}
              onChange={(e) => updateScenario(setterKey, 'intervention_cost', parseInt(e.target.value) || 0)}
              className="bg-white"
            />
          </div>

          {/* LEI Measure & Target Improvement Section */}
          <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Target className="h-4 w-4 text-teal-600" />
                LEI Measure & Target
              </Label>
              {/* Mode Toggle */}
              <div className="flex items-center gap-1 p-0.5 bg-slate-200 rounded-md">
                <button
                  onClick={() => setLeiMode('simple')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    currentLeiMode === 'simple'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Simple
                </button>
                <button
                  onClick={() => setLeiMode('advanced')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    currentLeiMode === 'advanced'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Advanced
                </button>
                <button
                  onClick={() => setLeiMode('import')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    currentLeiMode === 'import'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Import
                </button>
              </div>
            </div>
            
            {/* LEI Measure Selector */}
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Target LEI Measure</Label>
              <Select
                value={scenario.lei_measure_id}
                onValueChange={(v) => {
                  updateScenario(setterKey, 'lei_measure_id', v);
                  // If import mode, auto-fill from baseline
                  if (currentLeiMode === 'import' && importedBaselines?.baselines?.[v]) {
                    updateScenario(setterKey, 'current_maturity', importedBaselines.baselines[v].calculated_score);
                  }
                }}
              >
                <SelectTrigger className="bg-white text-sm">
                  <SelectValue placeholder="Select measure..." />
                </SelectTrigger>
                <SelectContent>
                  {[...leiMeasures].sort((a, b) => a.name.localeCompare(b.name)).map(measure => (
                    <SelectItem key={measure.id} value={measure.id}>
                      <div className="flex items-center gap-2">
                        <span>{measure.name}</span>
                        {currentLeiMode === 'import' && 
                         importedBaselines?.baselines?.[measure.id] && (
                          <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200">
                            {importedBaselines.baselines[measure.id].calculated_score?.toFixed(1)}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Import Mode Banner */}
            {currentLeiMode === 'import' && (
              <div className="text-xs p-2 bg-teal-50 border border-teal-200 rounded">
                {isLoadingBaselines ? (
                  <span className="text-teal-700">Loading imported baselines...</span>
                ) : importedBaselines?.count > 0 ? (
                  <span className="text-teal-700">
                    <span className="font-medium">{importedBaselines.count} measures</span> available from imports.
                    {importedBaselines?.baselines?.[scenario.lei_measure_id] && (
                      <span className="ml-1">
                        Current baseline: <span className="font-semibold">{importedBaselines.baselines[scenario.lei_measure_id].calculated_score?.toFixed(1)}</span>
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-amber-700">No imported data. Go to Data Import first.</span>
                )}
              </div>
            )}
            
            {/* LEI Improvement Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">Target Improvement</Label>
                <span className="text-sm font-semibold text-teal-600">{scenario.lei_improvement}%</span>
              </div>
              <Slider
                value={[scenario.lei_improvement]}
                onValueChange={([v]) => updateScenario(setterKey, 'lei_improvement', v)}
                min={0.5}
                max={25}
                step={0.5}
                className="py-2"
              />
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>Conservative</span>
                <span>Moderate</span>
                <span>Aggressive</span>
              </div>
            </div>
            
            {/* Modality + Measure Impact Preview */}
            {scenario.learning_modality && scenario.lei_measure_id && (
              <div className="mt-2 p-2 bg-white rounded border border-slate-100">
                <div className="flex items-center gap-2 text-xs">
                  <Sparkles className="h-3 w-3 text-purple-500" />
                  <span className="text-slate-600">Projected Impact:</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.label || 'Modality'}
                  </span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span className="text-xs text-slate-500">
                    {leiMeasures.find(m => m.id === scenario.lei_measure_id)?.name || 'Measure'}
                  </span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span className="text-xs font-medium text-teal-600">
                    +{scenario.lei_improvement}% uplift
                  </span>
                </div>
                <div className="mt-1 text-[10px] text-slate-400">
                  Adjusted by {MODALITY_OPTIONS.find(m => m.value === scenario.learning_modality)?.effectiveness || 100}% modality effectiveness
                </div>
              </div>
            )}
          </div>

          {/* Advanced Settings Collapsible */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                Advanced Settings
                {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-4">
              {/* Value Realisation Maturity */}
              <div className={`space-y-2 ${constraintLever === 'current_maturity' ? 'rounded-lg ring-1 ring-orange-300 bg-orange-50/50 p-2' : ''}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Maturity: {scenario.current_maturity}%</Label>
                  {constraintLever === 'current_maturity' && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-200 rounded-full px-1.5 py-px" data-testid={`constraint-lever-maturity-${scenarioKey}`}>Constraint lever</span>
                  )}
                </div>
                <Slider
                  value={[scenario.current_maturity]}
                  onValueChange={([v]) => updateScenario(setterKey, 'current_maturity', v)}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>

              {/* Execution Dampening */}
              <div className={`space-y-2 ${constraintLever === 'adoption_speed' ? 'rounded-lg ring-1 ring-orange-300 bg-orange-50/50 p-2' : ''}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Adoption Speed: {((scenario.dampening_factors?.adoption_speed || 0.7) * 100).toFixed(0)}%</Label>
                  {constraintLever === 'adoption_speed' && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-200 rounded-full px-1.5 py-px" data-testid={`constraint-lever-adoption-${scenarioKey}`}>Constraint lever</span>
                  )}
                </div>
                <Slider
                  value={[(scenario.dampening_factors?.adoption_speed || 0.7) * 100]}
                  onValueChange={([v]) => updateScenario(setterKey, 'dampening_factors.adoption_speed', v / 100)}
                  min={30}
                  max={100}
                  step={5}
                />
              </div>

              <div className={`space-y-2 ${constraintLever === 'leadership_alignment' ? 'rounded-lg ring-1 ring-orange-300 bg-orange-50/50 p-2' : ''}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Leadership Alignment: {((scenario.dampening_factors?.leadership_alignment || 0.8) * 100).toFixed(0)}%</Label>
                  {constraintLever === 'leadership_alignment' && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-200 rounded-full px-1.5 py-px" data-testid={`constraint-lever-leadership-${scenarioKey}`}>Constraint lever</span>
                  )}
                </div>
                <Slider
                  value={[(scenario.dampening_factors?.leadership_alignment || 0.8) * 100]}
                  onValueChange={([v]) => updateScenario(setterKey, 'dampening_factors.leadership_alignment', v / 100)}
                  min={30}
                  max={100}
                  step={5}
                />
              </div>

              {/* Time Lag Config */}
              <div className="space-y-2">
                <Label className="text-sm">Implementation Lag: {scenario.time_lag_config.implementation_lag_months} months</Label>
                <Slider
                  value={[scenario.time_lag_config.implementation_lag_months]}
                  onValueChange={([v]) => updateScenario(setterKey, 'time_lag_config.implementation_lag_months', v)}
                  min={0}
                  max={6}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Ramp-up Period: {scenario.time_lag_config.ramp_up_months} months</Label>
                <Slider
                  value={[scenario.time_lag_config.ramp_up_months]}
                  onValueChange={([v]) => updateScenario(setterKey, 'time_lag_config.ramp_up_months', v)}
                  min={1}
                  max={6}
                  step={1}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>
    );
  };

  // Render comparison results
  const renderComparisonResults = () => {
    if (!resultsA || !resultsB) return null;

    const roiA = resultsA.total_roi_percent || resultsA.roi_percent || 0;
    const roiB = resultsB.total_roi_percent || resultsB.roi_percent || 0;
    const roiDiff = getDifference(roiA, roiB);

    const impactA = resultsA.total_financial_impact || resultsA.financial_impact || 0;
    const impactB = resultsB.total_financial_impact || resultsB.financial_impact || 0;
    const impactDiff = getDifference(impactA, impactB);

    const combinedA = resultsA.combined_adjustment_factor || 1;
    const combinedB = resultsB.combined_adjustment_factor || 1;
    const combinedDiff = getDifference(combinedA, combinedB);

    return (
      <div className="space-y-6">
        {/* Key Metrics Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-teal-600" />
              Comparison Results
            </CardTitle>
            <CardDescription>
              Side-by-side analysis of key metrics and dampening factors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* ROI Comparison */}
              <div className="grid grid-cols-3 gap-4 items-center p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Scenario A</p>
                  <p className="text-2xl font-bold text-blue-600">{(roiA / 100).toFixed(2)}x</p>
                  <p className="text-xs text-slate-400">ROI</p>
                </div>
                <div className="text-center">
                  <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                    roiDiff.value > 0 ? 'bg-emerald-100 text-emerald-700' : 
                    roiDiff.value < 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {roiDiff.value > 0 ? <ArrowUpRight className="h-4 w-4" /> : 
                     roiDiff.value < 0 ? <ArrowDownRight className="h-4 w-4" /> : 
                     <Minus className="h-4 w-4" />}
                    {roiDiff.percent > 0 ? '+' : ''}{roiDiff.percent.toFixed(1)}%
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Difference</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Scenario B</p>
                  <p className="text-2xl font-bold text-amber-600">{(roiB / 100).toFixed(2)}x</p>
                  <p className="text-xs text-slate-400">ROI</p>
                </div>
              </div>

              {/* Financial Impact Comparison */}
              <div className="grid grid-cols-3 gap-4 items-center p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Scenario A</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(impactA, currency)}</p>
                  <p className="text-xs text-slate-400">Financial Impact</p>
                </div>
                <div className="text-center">
                  <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                    impactDiff.value > 0 ? 'bg-emerald-100 text-emerald-700' : 
                    impactDiff.value < 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {impactDiff.value > 0 ? <ArrowUpRight className="h-4 w-4" /> : 
                     impactDiff.value < 0 ? <ArrowDownRight className="h-4 w-4" /> : 
                     <Minus className="h-4 w-4" />}
                    {impactDiff.value > 0 ? '+' : ''}{formatCurrency(impactDiff.value, currency)}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Difference</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Scenario B</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(impactB, currency)}</p>
                  <p className="text-xs text-slate-400">Financial Impact</p>
                </div>
              </div>

              {/* Combined Adjustment Factor Comparison with Drilldown */}
              <div className="p-4 bg-gradient-to-r from-blue-50 via-slate-50 to-amber-50 rounded-lg border">
                <div className="grid grid-cols-3 gap-4 items-center mb-4">
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-1">Scenario A</p>
                    <p className="text-2xl font-bold text-blue-600">{(combinedA * 100).toFixed(1)}%</p>
                    <p className="text-xs text-slate-400">Combined Adjustment</p>
                  </div>
                  <div className="text-center">
                    <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                      combinedDiff.value > 0 ? 'bg-emerald-100 text-emerald-700' : 
                      combinedDiff.value < 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {combinedDiff.value > 0 ? <ArrowUpRight className="h-4 w-4" /> : 
                       combinedDiff.value < 0 ? <ArrowDownRight className="h-4 w-4" /> : 
                       <Minus className="h-4 w-4" />}
                      {combinedDiff.percent > 0 ? '+' : ''}{combinedDiff.percent.toFixed(1)}%
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Difference</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-1">Scenario B</p>
                    <p className="text-2xl font-bold text-amber-600">{(combinedB * 100).toFixed(1)}%</p>
                    <p className="text-xs text-slate-400">Combined Adjustment</p>
                  </div>
                </div>

                {/* Factor Drilldown */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3 text-center">Factor Breakdown</p>
                  <div className="grid grid-cols-4 gap-2">
                    {/* Scale Factor */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="p-3 bg-white rounded-lg border cursor-help hover:border-amber-300 transition-colors">
                            <div className="flex items-center justify-center gap-1 mb-2">
                              <Scale className="h-4 w-4 text-amber-600" />
                              <span className="text-xs font-medium text-slate-600">Scale</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-600 font-medium">
                                {((getFactorValue(resultsA, 'scale') || 1) * 100).toFixed(0)}%
                              </span>
                              <span className="text-amber-600 font-medium">
                                {((getFactorValue(resultsB, 'scale') || 1) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-medium mb-1">Scale Dampening</p>
                          <p className="text-xs">A: {resultsA.scale_dampening?.bracket || 'N/A'}</p>
                          <p className="text-xs">B: {resultsB.scale_dampening?.bracket || 'N/A'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Industry Factor */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="p-3 bg-white rounded-lg border cursor-help hover:border-blue-300 transition-colors">
                            <div className="flex items-center justify-center gap-1 mb-2">
                              <Factory className="h-4 w-4 text-blue-600" />
                              <span className="text-xs font-medium text-slate-600">Industry</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-600 font-medium">
                                {((getFactorValue(resultsA, 'industry') || 1) * 100).toFixed(0)}%
                              </span>
                              <span className="text-amber-600 font-medium">
                                {((getFactorValue(resultsB, 'industry') || 1) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-medium mb-1">Industry Modifier</p>
                          <p className="text-xs">A: {resultsA.industry_modifier?.industry || 'N/A'}</p>
                          <p className="text-xs">B: {resultsB.industry_modifier?.industry || 'N/A'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Modality Factor */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="p-3 bg-white rounded-lg border cursor-help hover:border-purple-300 transition-colors">
                            <div className="flex items-center justify-center gap-1 mb-2">
                              <GraduationCap className="h-4 w-4 text-purple-600" />
                              <span className="text-xs font-medium text-slate-600">Modality</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-600 font-medium">
                                {((getFactorValue(resultsA, 'modality') || 1) * 100).toFixed(0)}%
                              </span>
                              <span className="text-amber-600 font-medium">
                                {((getFactorValue(resultsB, 'modality') || 1) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-medium mb-1">Learning Modality</p>
                          <p className="text-xs">A: {resultsA.learning_modality?.modality || 'N/A'}</p>
                          <p className="text-xs">B: {resultsB.learning_modality?.modality || 'N/A'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Time Lag Factor */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="p-3 bg-white rounded-lg border cursor-help hover:border-teal-300 transition-colors">
                            <div className="flex items-center justify-center gap-1 mb-2">
                              <Timer className="h-4 w-4 text-teal-600" />
                              <span className="text-xs font-medium text-slate-600">Time Lag</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-600 font-medium">
                                {((getFactorValue(resultsA, 'time_lag') || 1) * 100).toFixed(0)}%
                              </span>
                              <span className="text-amber-600 font-medium">
                                {((getFactorValue(resultsB, 'time_lag') || 1) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-medium mb-1">Time Lag Factor</p>
                          <p className="text-xs">A: {resultsA.time_lag?.effective_months || 0} effective months</p>
                          <p className="text-xs">B: {resultsB.time_lag?.effective_months || 0} effective months</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>

              {/* Theoretical vs Realistic */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm font-medium text-blue-700 mb-2">Scenario A Impact</p>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Theoretical:</span>
                    <span className="font-medium">{formatCurrency(resultsA.total_financial_impact_raw || impactA / combinedA, currency)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Realistic:</span>
                    <span className="font-medium text-blue-600">{formatCurrency(impactA, currency)}</span>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm font-medium text-amber-700 mb-2">Scenario B Impact</p>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Theoretical:</span>
                    <span className="font-medium">{formatCurrency(resultsB.total_financial_impact_raw || impactB / combinedB, currency)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Realistic:</span>
                    <span className="font-medium text-amber-600">{formatCurrency(impactB, currency)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insights */}
        <Card className="border-teal-200 bg-teal-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-teal-700">
              <Info className="h-5 w-5" />
              Key Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {roiDiff.value > 0 && (
                <li className="flex items-start gap-2">
                  <ArrowUpRight className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Scenario B delivers <strong>{roiDiff.percent.toFixed(1)}% higher ROI</strong> due to different dampening factors</span>
                </li>
              )}
              {roiDiff.value < 0 && (
                <li className="flex items-start gap-2">
                  <ArrowDownRight className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
                  <span>Scenario A delivers <strong>{Math.abs(roiDiff.percent).toFixed(1)}% higher ROI</strong> due to different dampening factors</span>
                </li>
              )}
              {Math.abs(impactDiff.value) > 100000 && (
                <li className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span>Financial impact difference of <strong>{formatCurrency(Math.abs(impactDiff.value), currency)}</strong></span>
                </li>
              )}
              {getFactorValue(resultsA, 'scale') !== getFactorValue(resultsB, 'scale') && (
                <li className="flex items-start gap-2">
                  <Scale className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Scale dampening varies: {((getFactorValue(resultsA, 'scale') || 1) * 100).toFixed(0)}% vs {((getFactorValue(resultsB, 'scale') || 1) * 100).toFixed(0)}% based on organization size</span>
                </li>
              )}
              {getFactorValue(resultsA, 'industry') !== getFactorValue(resultsB, 'industry') && (
                <li className="flex items-start gap-2">
                  <Factory className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Industry modifier difference: {resultsA.industry_modifier?.industry} ({((getFactorValue(resultsA, 'industry') || 1) * 100).toFixed(0)}%) vs {resultsB.industry_modifier?.industry} ({((getFactorValue(resultsB, 'industry') || 1) * 100).toFixed(0)}%)</span>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
    );
  };

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Dampening What-If"
        description="Compare scenarios with different dampening configurations side-by-side. Analyze how changes to scale, industry, transfer rate, and execution factors impact your ROI projections."
        theme={__pageTheme}
        icon={Scale}
        testId="dampening-hero"
        actions={(
          <div className="flex gap-2">
            <Link
              to="/roe-reference#deep-dives"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-xs font-medium backdrop-blur-sm transition-colors"
              data-testid="dampening-hero-reference-btn"
            >
              <BookOpen className="h-3.5 w-3.5" /> ROE Reference
            </Link>
            <Button
              variant="outline"
              onClick={() => setSavePresetDialogOpen(true)}
              disabled={!scenarioA || !scenarioB}
              className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="dampening-save-preset-btn"
            >
              <BookmarkPlus className="h-4 w-4 mr-2" />
              Save Preset
            </Button>
            <Button
              variant="outline"
              onClick={resetScenarios}
              className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="dampening-reset-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={runComparison}
              disabled={isCalculating}
              className="bg-white text-slate-900 hover:bg-slate-50 font-semibold"
              data-testid="dampening-run-btn"
            >
              {isCalculating ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run Comparison
                </>
              )}
            </Button>
          </div>
        )}
      >
        {/* Inline status tiles inside the hero band — surfaces
            the active analysis mode and the configuration state
            of each scenario at a glance, replacing the green
            secondary header that used to sit below the hero. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4" data-testid="dampening-hero-status">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Analysis Mode</p>
            <p className="text-xl font-bold capitalize mt-0.5 text-slate-900">
              {analysisMode === 'single' ? 'Single Measure' : analysisMode === 'portfolio' ? 'Portfolio' : analysisMode}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Scenario A</p>
            <p className="text-xl font-bold mt-0.5 text-slate-900">{scenarioA ? 'Configured' : 'Not Set'}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Scenario B</p>
            <p className="text-xl font-bold mt-0.5 text-slate-900">{scenarioB ? 'Configured' : 'Not Set'}</p>
          </div>
        </div>
      </PageHero>
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6">
      {/* Constraint context — which chain stage is leaking and which
          scenario lever models it. Self-hides without a constraint. */}
      {chainLeak && <ValueTransferContextStrip leak={chainLeak} leakPct={leakPct} />}

      {/* Comparison Presets */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Quick Start Presets
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowPresets(!showPresets)}
              className="text-xs"
            >
              {showPresets ? 'Hide' : 'Show'} Presets
              {showPresets ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
          </div>
          <CardDescription className="text-xs">
            Load pre-configured comparison scenarios to quickly analyze different execution factors
          </CardDescription>
        </CardHeader>
        {showPresets && (
          <CardContent className="pt-0">
            {/* System Presets */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">System Presets</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {SYSTEM_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => loadPreset(preset, true)}
                    className={`p-3 rounded-lg border-2 border-slate-200 hover:border-slate-300 transition-all text-left group hover:shadow-sm ${preset.bgColor}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-md bg-white shadow-sm ${preset.color}`}>
                        <preset.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 text-sm group-hover:text-slate-900">{preset.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{preset.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* User Presets */}
            {userPresets.length > 0 && (
              <div className="space-y-3 mt-6 pt-4 border-t">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Your Saved Presets</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {userPresets.map(preset => (
                    <div
                      key={preset.preset_id}
                      className="p-3 rounded-lg border-2 border-slate-200 hover:border-slate-300 transition-all bg-white group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          onClick={() => loadPreset(preset)}
                          className="flex-1 text-left"
                        >
                          <div className="flex items-start gap-2">
                            <div className="p-1.5 rounded-md bg-slate-100 text-slate-600">
                              <Bookmark className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-700 text-sm group-hover:text-slate-900">{preset.name}</p>
                              {preset.description && (
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{preset.description}</p>
                              )}
                            </div>
                          </div>
                        </button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteUserPreset(preset.preset_id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Analysis Mode Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-slate-700">Analysis Mode</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {ANALYSIS_MODES.map(mode => (
              <button
                key={mode.id}
                onClick={() => setAnalysisMode(mode.id)}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  analysisMode === mode.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className={`font-medium ${analysisMode === mode.id ? 'text-teal-700' : 'text-slate-700'}`}>
                  {mode.label}
                </p>
                <p className="text-xs text-slate-500 mt-1">{mode.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scenario Configuration */}
      <div className="grid grid-cols-2 gap-6">
        {renderScenarioConfig(scenarioA, 'A', 'Scenario A', 'blue')}
        {renderScenarioConfig(scenarioB, 'B', 'Scenario B', 'amber')}
      </div>

      {/* Results */}
      {renderComparisonResults()}

      {/* Analytics Summary Section */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-purple-500" />
              Comparison Insights
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/comparison-analytics')}
                className="text-xs text-purple-600 hover:text-purple-700"
              >
                View Full Analytics
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAnalytics(!showAnalytics)}
                className="text-xs"
              >
                {showAnalytics ? 'Hide' : 'Show'}
                {showAnalytics ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showAnalytics && (
          <CardContent className="pt-0">
            {isLoadingAnalytics ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Analytics */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-slate-700">Your Activity</span>
                  </div>
                  
                  {personalAnalytics?.total_comparisons > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-600">{personalAnalytics.total_comparisons}</p>
                          <p className="text-xs text-slate-500">Comparisons Run</p>
                        </div>
                        <div className="p-3 bg-emerald-50 rounded-lg">
                          <p className="text-2xl font-bold text-emerald-600">
                            {personalAnalytics.avg_roi_difference > 0 ? '+' : ''}{personalAnalytics.avg_roi_difference?.toFixed(0) || 0}%
                          </p>
                          <p className="text-xs text-slate-500">Avg ROI Difference</p>
                        </div>
                      </div>
                      
                      {/* Most Used Presets */}
                      {personalAnalytics.preset_usage?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Your Most Used Presets</p>
                          <div className="space-y-1">
                            {personalAnalytics.preset_usage.slice(0, 3).map((preset, idx) => (
                              <div key={`preset-${preset.name}-${idx}`} className="flex items-center justify-between text-xs">
                                <span className="text-slate-600 truncate">{preset.name}</span>
                                <span className="text-slate-400">{preset.count}x</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Factor Rankings */}
                      {personalAnalytics.factor_ranking?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Factors with Biggest Impact (Your Data)</p>
                          <div className="space-y-1">
                            {personalAnalytics.factor_ranking.slice(0, 3).map(([factor, impact], idx) => (
                              <div key={factor} className="flex items-center gap-2">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                  idx === 1 ? 'bg-slate-100 text-slate-600' :
                                  'bg-amber-50 text-amber-600'
                                }`}>
                                  {idx + 1}
                                </span>
                                <span className="text-xs text-slate-600 flex-1">{factor?.replace(/_/g, ' ') || 'Unknown'}</span>
                                <span className="text-xs font-medium text-purple-600">{(impact ?? 0).toFixed(0)}% avg</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-slate-500">
                      <PieChart className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>No comparisons yet</p>
                      <p className="text-xs">Run comparisons to build your analytics</p>
                    </div>
                  )}
                </div>

                {/* Org-wide Analytics */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-teal-500" />
                    <span className="text-sm font-medium text-slate-700">Community Insights</span>
                  </div>
                  
                  {aggregateAnalytics?.total_comparisons > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-teal-50 rounded-lg">
                          <p className="text-2xl font-bold text-teal-600">{aggregateAnalytics.total_comparisons}</p>
                          <p className="text-xs text-slate-500">Total Comparisons</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-2xl font-bold text-purple-600">{aggregateAnalytics.unique_users}</p>
                          <p className="text-xs text-slate-500">Users Analyzing</p>
                        </div>
                      </div>
                      
                      {/* Popular Presets */}
                      {aggregateAnalytics.popular_presets?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Most Popular Presets</p>
                          <div className="space-y-1">
                            {aggregateAnalytics.popular_presets.slice(0, 3).map((preset, idx) => (
                              <div key={`popular-${preset.name}-${idx}`} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1">
                                  {idx === 0 && <Star className="h-3 w-3 text-yellow-500" />}
                                  <span className="text-slate-600 truncate">{preset.name}</span>
                                </div>
                                <span className="text-slate-400">{preset.usage_percent}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Factor Impact Ranking */}
                      {aggregateAnalytics.factor_impact_ranking?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Highest Impact Factors (All Users)</p>
                          <div className="space-y-1">
                            {aggregateAnalytics.factor_impact_ranking.slice(0, 3).map((f, idx) => (
                              <div key={f?.factor || idx} className="flex items-center gap-2">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                  idx === 1 ? 'bg-slate-100 text-slate-600' :
                                  'bg-amber-50 text-amber-600'
                                }`}>
                                  {idx + 1}
                                </span>
                                <span className="text-xs text-slate-600 flex-1">{f?.display_name || 'Unknown'}</span>
                                <span className="text-xs font-medium text-teal-600">{(f?.avg_roi_impact ?? 0).toFixed(0)}% avg</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Insights */}
                      {aggregateAnalytics.insights?.length > 0 && (
                        <div className="p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-100">
                          <div className="flex items-start gap-2">
                            <Lightbulb className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-700">{aggregateAnalytics.insights[0]?.message}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-slate-500">
                      <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>No community data yet</p>
                      <p className="text-xs">Be the first to contribute!</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Trend Insights Row */}
            {trendsData?.insights?.length > 0 && !isLoadingAnalytics && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm font-medium text-slate-700">Your Trends (Last 30 Days)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {trendsData.insights.slice(0, 3).map((insight, idx) => (
                    <div 
                      key={`insight-${insight.sentiment}-${idx}`}
                      className={`p-3 rounded-lg border-l-4 ${
                        insight.sentiment === 'positive' ? 'border-l-emerald-500 bg-emerald-50/50' :
                        insight.sentiment === 'suggestion' ? 'border-l-amber-500 bg-amber-50/50' :
                        'border-l-slate-300 bg-slate-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`p-1.5 rounded-full flex-shrink-0 ${
                          insight.sentiment === 'positive' ? 'bg-emerald-100' :
                          insight.sentiment === 'suggestion' ? 'bg-amber-100' :
                          'bg-slate-100'
                        }`}>
                          {insight.icon === 'trending_up' && <TrendingUp className="h-3 w-3 text-emerald-600" />}
                          {insight.icon === 'trending_down' && <TrendingDown className="h-3 w-3 text-rose-500" />}
                          {insight.icon === 'activity' && <Activity className="h-3 w-3 text-purple-600" />}
                          {insight.icon === 'arrow_up' && <ArrowUpRight className="h-3 w-3 text-emerald-600" />}
                          {insight.icon === 'sparkles' && <Sparkles className="h-3 w-3 text-amber-600" />}
                          {insight.icon === 'lightbulb' && <Lightbulb className="h-3 w-3 text-amber-600" />}
                          {insight.icon === 'target' && <Target className="h-3 w-3 text-blue-600" />}
                          {insight.icon === 'minus' && <Minus className="h-3 w-3 text-slate-500" />}
                        </div>
                        <p className="text-xs text-slate-700">{insight.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Goals Progress Widget */}
            {goalsSummary?.active_goals > 0 && !isLoadingAnalytics && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-purple-500" />
                    <span className="text-sm font-medium text-slate-700">Your Goals</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/goals')}
                    className="text-xs text-purple-600 hover:text-purple-700"
                  >
                    View All Goals
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </div>
                <div className="space-y-3">
                  {goalsSummary.goals_summary?.slice(0, 3).map((goal) => (
                    <div key={goal.goal_id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700 truncate">{goal.title}</span>
                        <Badge 
                          variant="outline" 
                          className={goal.on_track ? 'text-emerald-600 border-emerald-200' : 'text-amber-600 border-amber-200'}
                        >
                          {goal.on_track ? 'On Track' : `${goal.days_remaining}d left`}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${goal.on_track ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(goal.progress_percent, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600 w-12 text-right">
                          {(goal.progress_percent ?? 0).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="h-5 w-5 text-teal-600" />
              Save Comparison Preset
            </DialogTitle>
            <DialogDescription>
              Save your current scenario configurations as a reusable preset
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., Q1 Training Initiative Comparison"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="preset-description">Description (optional)</Label>
              <Input
                id="preset-description"
                placeholder="Brief description of what this preset compares"
                value={newPresetDescription}
                onChange={(e) => setNewPresetDescription(e.target.value)}
              />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p className="font-medium text-slate-700 mb-2">This preset will save:</p>
              <ul className="space-y-1 text-slate-600 text-xs">
                <li>• <span className="text-blue-600">Scenario A:</span> {scenarioA?.name || 'Scenario A'}</li>
                <li>• <span className="text-amber-600">Scenario B:</span> {scenarioB?.name || 'Scenario B'}</li>
                <li>• Analysis mode: {ANALYSIS_MODES.find(m => m.id === analysisMode)?.label}</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveUserPreset} disabled={isSavingPreset || !newPresetName.trim()}>
              {isSavingPreset ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Bookmark className="h-4 w-4 mr-2" />
                  Save Preset
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
};

export default DampeningComparisonPage;
